/** Firebase Auth helpers — anonymous bootstrap, email/password, and Google. */
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  linkWithCredential,
  linkWithPopup,
  EmailAuthProvider,
  signOut as fbSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail as fbSendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  type User as FirebaseUser,
  type UserCredential,
} from 'firebase/auth';
import { auth } from './firebase';

export type { FirebaseUser };

export const onAuthChange = (cb: (u: FirebaseUser | null) => void) =>
  onAuthStateChanged(auth, cb);

/**
 * Thrown when linking a real credential onto the current anonymous session
 * fails because that credential already belongs to a different, existing
 * account. `retry()` performs the plain (non-linking) sign-in the caller can
 * offer as "sign in and abandon this session's garage" — the anon uid and
 * anything saved under it are not preserved in that path.
 */
export class AccountMergeRequiredError extends Error {
  retry: () => Promise<FirebaseUser>;
  constructor(retry: () => Promise<FirebaseUser>) {
    super('An account with this email or provider already exists.');
    this.retry = retry;
  }
}

const MERGE_CONFLICT_CODES = new Set([
  'auth/credential-already-in-use',
  'auth/email-already-in-use',
]);

/** Ensures there is always a signed-in Firebase user, anonymous or real. Call once on app mount. */
export async function ensureSignedIn(): Promise<FirebaseUser> {
  if (auth.currentUser) return auth.currentUser;
  const cred = await signInAnonymously(auth);
  return cred.user;
}

export async function signIn(email: string, password: string): Promise<FirebaseUser> {
  const normalizedEmail = email.trim().toLowerCase();
  const current = auth.currentUser;
  if (current?.isAnonymous) {
    try {
      const credential = EmailAuthProvider.credential(normalizedEmail, password);
      const result = await linkWithCredential(current, credential);
      return result.user;
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      if (MERGE_CONFLICT_CODES.has(code)) {
        throw new AccountMergeRequiredError(async () => {
          const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
          return result.user;
        });
      }
      throw err;
    }
  }
  const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  return result.user;
}

export async function signUp(email: string, password: string): Promise<FirebaseUser> {
  const normalizedEmail = email.trim().toLowerCase();
  const current = auth.currentUser;
  if (current?.isAnonymous) {
    try {
      const credential = EmailAuthProvider.credential(normalizedEmail, password);
      const result = await linkWithCredential(current, credential);
      return result.user;
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      if (MERGE_CONFLICT_CODES.has(code)) {
        throw new AccountMergeRequiredError(async () => {
          const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
          return result.user;
        });
      }
      throw err;
    }
  }
  const result = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
  return result.user;
}

/** Re-anonymizes so the app stays functional (guest browsing, garage-building) after sign-out. */
export async function signOut(): Promise<void> {
  await fbSignOut(auth);
  await signInAnonymously(auth);
}

export async function sendPasswordReset(email: string) {
  await fbSendPasswordResetEmail(auth, email.trim().toLowerCase());
}

export async function signInWithGoogle(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  const current = auth.currentUser;
  if (current?.isAnonymous) {
    try {
      return await linkWithPopup(current, provider);
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      if (MERGE_CONFLICT_CODES.has(code)) {
        throw new AccountMergeRequiredError(async () => {
          const result = await signInWithPopup(auth, provider);
          return result.user;
        });
      }
      throw err;
    }
  }
  return signInWithPopup(auth, provider);
}

export function authErrorMessage(err: unknown): string {
  if (err instanceof AccountMergeRequiredError) return err.message;
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Wrong email or password.';
    case 'auth/user-not-found':
      return 'No account with this email. Try creating one.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in cancelled.';
    default:
      return (err as Error)?.message ?? 'Something went wrong.';
  }
}
